/**
 * The club's photo rule (setting `media_required`, owner, 12.10.2026): when
 * the club admin switches it on, a member's report needs at least one photo
 * or video. Technicians and admins may still record without one (they enter
 * damage from paper lists, for instance). One decision for API, demo store,
 * web and app — the API enforces it, the clients only stop the person earlier
 * and say why.
 *
 * Photos are uploaded after the report is created (and, without signal,
 * hours later from the app's outbox), so the report states how many will
 * follow (`mediaCount`); the server cannot wait for bytes it does not have.
 */
import { type MemberRole, hasAtLeastRole } from './status';
import { MAX_IMAGES_PER_DAMAGE } from './validation';
import { VIDEO_MAX_PER_DAMAGE } from './video';

/** A club without the setting behaves as before it existed. */
export const DEFAULT_MEDIA_REQUIRED = false;

export function mediaRequirementMet(role: MemberRole, required: boolean | null | undefined, mediaCount: number): boolean {
  if (required !== true) return true;
  if (hasAtLeastRole(role, 'editor')) return true;
  return Number.isInteger(mediaCount) && mediaCount >= 1;
}

/**
 * Who may add photos or videos to a damage that already exists (owner,
 * 17.10.2026: „Melder jederzeit + Technik“). The reporter may hand in a
 * better picture at any time — also after the Technik has reviewed the
 * report — the Technik and the admins always; anybody else reports
 * „derselbe Schaden“ instead. A deleted damage takes nothing. Member
 * participation (rule 35) is asked separately, as for every member write.
 * One decision for API, demo store, web and app.
 */
export function mayAddMedia(
  role: MemberRole,
  actorId: string,
  damage: { readonly reportedBy: string; readonly deleted: boolean },
): boolean {
  if (damage.deleted || role === 'inactive') return false;
  return hasAtLeastRole(role, 'editor') || damage.reportedBy === actorId;
}

export interface MediaCounts {
  readonly photos: number;
  readonly videos: number;
}

/**
 * How many more photos and videos fit on a damage: the limits minus what is
 * stored and what is still waiting to go up (the app's outbox). Never
 * negative — a damage that took another report's photos can hold more.
 */
export function mediaSlotsLeft(
  stored: MediaCounts,
  pending: MediaCounts = { photos: 0, videos: 0 },
): MediaCounts {
  return {
    photos: Math.max(0, MAX_IMAGES_PER_DAMAGE - stored.photos - pending.photos),
    videos: Math.max(0, VIDEO_MAX_PER_DAMAGE - stored.videos - pending.videos),
  };
}
